/* PET Can Europe - pallet preview in the cart (assets/pallet-cart-preview.js)
 *
 * Lines of the hidden 'pallet-builder-line' product get a drawing instead of
 * the flat product photo, in the cart page and in the cart drawer.
 *
 * 24 Sep 2026 - one drawing per pallet configuration
 *   The pallet builder now adds ONE line per size plus one delivery line per
 *   pallet type, all carrying the same configuration id (visible property
 *   "Pallet configuration", e.g. PB-260924-K7QX, and hidden _pb_id) and the
 *   full configuration as hidden _pb_json. So:
 *   · the FIRST line of a configuration (per cart / drawer) shows the whole
 *     configuration: every pallet side by side, layers bottom -> top, in the
 *     size colours, half pallets on one half of the deck;
 *   · the other size lines of that configuration show a single carton in the
 *     size colour, the delivery line a pallet footprint (60x80 / 120x80).
 *   _pb_json is read from /cart.js (hidden properties are not in the HTML).
 *   Without it (or before it has loaded) the drawing is rebuilt from the lines
 *   themselves: boxes per size stacked in full layers, max 195 cm per pallet.
 *   Older lines without a configuration id keep the old drawing per line.
 *
 * Packing data (boxes per layer, layers, layer height, colour, name) comes
 * from window.PCE (snippets/pce-data.liquid, metaobjects pce_blikformaat).
 * A cart line is linked to a size via PCE.pb_lines: option-1 value of the
 * variant -> code, derived from the SKU (renaming an option breaks nothing).
 * Checkout, confirmation e-mail and order status still show the product
 * image: Shopify renders those and a theme cannot draw there.
 *
 * 24 Sep 2026 (b) - the ONE line from PetCan Ops
 *   The builder now normally adds ONE line: a hidden product made by PetCan
 *   Ops per configuration (handle "pallet-op-maat-…", title "Pallet
 *   configuration – 1 full + 1 half pallet (…)"), with _pb_id and _pb_json in
 *   its properties. That line gets the whole configuration drawing from
 *   _pb_json (pallets side by side). Until /cart.js has loaded, or without
 *   _pb_json, it keeps its product photo. The lines per size above stay as
 *   the fallback and are drawn as before.
 */
(function () {
  'use strict';

  var HANDLE = 'pallet-builder-line';
  var ONE_LINE_RE = /^pallet-op-maat-/; /* products made by PetCan Ops (/api/pallet) */
  var ID_RE = /\bPB-\d{6}-[A-Z0-9]{4}\b/;
  var MAX_H = 195, BASE_H = 14.5; /* cm: max. pallet height, pallet itself */

  /* ---------- data ---------- */
  function codeOf(lineText) {
    var P = window.PCE;
    if (!P) return null;
    if (P.pb_lines && P.pb_lines[lineText]) return P.pb_lines[lineText];
    var hit = (P.sizes || []).filter(function (s) { return s.naam === lineText; })[0];
    return hit ? hit.code : null;
  }
  function specOf(code) {
    var s = window.PCE && window.PCE.byCode && window.PCE.byCode[code];
    if (!s || !s.dozen_per_laag || !s.lagen_per_pallet || !s.laaghoogte_cm) return null;
    if (window.PCE.settings && window.PCE.settings.max_pallethoogte_cm) MAX_H = window.PCE.settings.max_pallethoogte_cm;
    return { code: code, name: s.naam || code, perBox: s.blikjes_per_doos, perLayer: s.dozen_per_laag,
      layers: s.lagen_per_pallet, layerH: s.laaghoogte_cm, color: s.kleur || '#6c5bc4' };
  }

  /* configurations from /cart.js: { 'PB-260924-K7QX': {pallets:[...], ...} } */
  var CONFIGS = {}, loading = null, loadedOnce = false;
  function loadConfigs() {
    if (loading) return;
    loading = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var map = {};
        (cart.items || []).forEach(function (it) {
          var p = it.properties || {};
          if (p._pb_id && p._pb_json && !map[p._pb_id]) { try { map[p._pb_id] = JSON.parse(p._pb_json); } catch (e) {} }
        });
        CONFIGS = map;
      })
      .catch(function () {})
      .then(function () { loading = null; loadedOnce = true; forceRepaint(); });
  }

  /* ---------- drawing helpers (76 x 92 viewBox, 0.40 px per cm) ---------- */
  var K = 0.40, X0 = 10, W = 56, DECK = 80.2;
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function r(x, y, w, h, fill, extra) {
    return '<rect x="' + x.toFixed(2) + '" y="' + y.toFixed(2) + '" width="' + Math.max(0, w).toFixed(2) +
      '" height="' + h.toFixed(2) + '" fill="' + fill + '"' + (extra || '') + '/>';
  }
  function palletBase(x, w) {
    var bw = Math.min(8, w / 4);
    return r(x, DECK, w, 2.1, '#b7885a') + r(x, 82.3, bw, 3.7, '#8f6740') +
      r(x + (w - bw) / 2, 82.3, bw, 3.7, '#8f6740') + r(x + w - bw, 82.3, bw, 3.7, '#8f6740');
  }
  function svg(label, inner) {
    return '<svg viewBox="0 0 76 92" width="100%" height="100%" role="img" aria-label="' + esc(label) + '">' + inner + '</svg>';
  }

  /* the whole configuration: pallets side by side */
  function configSvg(cfg, id) {
    var pallets = cfg.pallets || [], n = Math.max(1, pallets.length), gap = 3;
    var left = 6, span = 64, colW = (span - gap * (n - 1)) / n, out = '', boxes = 0;
    pallets.forEach(function (p, i) {
      var x = left + i * (colW + gap), half = p.format === 'half', lane = half ? colW / 2 : colW, y = DECK;
      /* room up to the height limit, dashed */
      var capH = (MAX_H - BASE_H) * K;
      out += r(x, DECK - capH, colW, capH, '#faf8f5', ' stroke="#cfc9bd" stroke-width="0.6" stroke-dasharray="2 2"');
      (p.layers || []).forEach(function (l) {
        var s = specOf(l[0]);
        if (!s) return;
        var cap = half ? Math.max(1, Math.floor(s.perLayer / 2)) : s.perLayer, h = s.layerH * K, w = lane * Math.min(1, l[1] / cap);
        y -= h; boxes += l[1];
        out += r(x, y, w, h, s.color, ' stroke="#fff" stroke-width="0.6"');
        if (w < lane - 0.01) out += r(x + w, y, lane - w, h, s.color, ' fill-opacity="0.16" stroke="#fff" stroke-width="0.6" stroke-dasharray="1.5 1.5"');
      });
      out += palletBase(x, colW);
    });
    return svg((id ? id + ': ' : '') + pallets.length + ' pallet' + (pallets.length === 1 ? '' : 's') + ', ' + boxes + ' boxes', out);
  }

  /* fallback when _pb_json is not available: rebuild from the lines
     (sizes -> full layers, tallest first, max. height per pallet) */
  function configFromRows(rows) {
    var counts = {}, units = [], pallets = [];
    rows.forEach(function (row) {
      var code = codeOf(optionsOf(row)[0] || '');
      if (code && code.charAt(0) !== '_' && specOf(code)) counts[code] = (counts[code] || 0) + quantityOf(row);
    });
    Object.keys(counts).forEach(function (code) {
      var s = specOf(code), n = counts[code];
      while (n > 0) { var b = Math.min(n, s.perLayer); units.push({ code: code, boxes: b, h: s.layerH, partial: b < s.perLayer }); n -= b; }
    });
    units.sort(function (a, b) { return (a.partial - b.partial) || (b.h - a.h); });
    units.forEach(function (u) {
      var p = pallets.filter(function (q) { return q.load + u.h <= MAX_H - BASE_H + 1e-9; })[0];
      if (!p) { p = { format: 'full', layers: [], load: 0 }; pallets.push(p); }
      p.layers.push([u.code, u.boxes]); p.load += u.h;
    });
    return { pallets: pallets };
  }

  /* one carton in the size colour (other size lines of a configuration) */
  function boxSvg(s, qty) {
    var out = r(16, 30, 44, 44, s.color, ' rx="2"') + r(16, 30, 44, 7, '#000', ' fill-opacity="0.12"') +
      r(35, 30, 6, 44, '#fff', ' fill-opacity="0.35"') +
      '<text x="38" y="62" text-anchor="middle" font-size="11" font-weight="700" fill="#fff">' + qty + '×</text>';
    return svg(qty + ' × ' + s.name, out);
  }

  /* delivery line: pallet footprint */
  function deliverySvg(optionText, qty) {
    var isFull = /full|120/i.test(optionText || '');
    var w = isFull ? 56 : 30, x = isFull ? X0 : 23, label = isFull ? '120 × 80' : '60 × 80';
    var out = r(x, DECK - 34, w, 34, '#eeece7', ' stroke="#c4bdb0" stroke-width="1" stroke-dasharray="3 3"') + palletBase(x, w) +
      '<text x="38" y="' + (qty > 1 ? 58 : 64) + '" text-anchor="middle" font-size="8" font-weight="700" fill="#5b6068">' + label + '</text>' +
      '<text x="38" y="' + (qty > 1 ? 67 : 73) + '" text-anchor="middle" font-size="6.5" fill="#8a8f97">cm</text>' +
      (qty > 1 ? '<text x="38" y="76" text-anchor="middle" font-size="7.5" font-weight="700" fill="#171a1f">' + qty + '×</text>' : '');
    return svg((qty > 1 ? qty + ' × ' : '') + label + ' cm pallet, delivered with tail-lift', out);
  }

  /* legacy lines (no configuration id): that line's boxes on one pallet */
  function stackSvg(s, boxes) {
    var cap = s.perLayer * s.layers, shown = Math.min(boxes, cap), full = Math.floor(shown / s.perLayer), rest = shown % s.perLayer;
    var lh = s.layerH * K, capH = s.layers * lh;
    var out = r(X0, DECK - capH, W, capH, '#faf8f5', ' stroke="#cfc9bd" stroke-width="1" stroke-dasharray="3 3"');
    for (var i = 1; i <= full; i++) out += r(X0, DECK - i * lh, W, lh, s.color, ' stroke="#fff" stroke-width="0.8"');
    if (rest > 0) {
      var y = DECK - (full + 1) * lh, rw = Math.round(W * rest / s.perLayer);
      out += r(X0, y, rw, lh, s.color, ' stroke="#fff" stroke-width="0.8"');
      out += r(X0 + rw, y, W - rw, lh, s.color, ' fill-opacity="0.16" stroke="#fff" stroke-width="0.8" stroke-dasharray="2 2"');
    }
    out += palletBase(X0, W);
    return svg(boxes + ' of ' + cap + ' boxes ' + s.name + ' on a EUR pallet, ' + Math.floor(shown / cap * 100) + '% full', out);
  }

  /* ---------- reading a cart row ---------- */
  function handleOf(row) {
    if (row.dataset && row.dataset.handle) return row.dataset.handle;
    var a = row.querySelector('a.cart-item__title') || row.querySelector('a[href*="/products/"]');
    var m = a && (a.getAttribute('href') || '').match(/\/products\/([^?#/]+)/);
    return m ? m[1] : '';
  }
  function optionsOf(row) {
    return Array.prototype.map.call(row.querySelectorAll('.cart-item__option-value'), function (el) { return el.textContent.replace(/,\s*$/, '').trim(); });
  }
  function quantityOf(row) {
    if (row.dataset && row.dataset.quantity) return parseInt(row.dataset.quantity, 10) || 0;
    var input = row.querySelector('input[name="updates[]"], input[name="quantity"]');
    return input ? parseInt(input.value, 10) || 0 : 0;
  }
  /* configuration id from the visible property (label may be translated) */
  function configIdOf(row) {
    var dds = row.querySelectorAll('dd');
    for (var i = 0; i < dds.length; i++) { var m = dds[i].textContent.match(ID_RE); if (m) return m[0]; }
    var opts = row.querySelector('.cart-item__options');
    var m2 = opts && opts.textContent.match(ID_RE);
    return m2 ? m2[0] : '';
  }
  function slotFor(row) {
    var media = row.querySelector('.cart-item__media');
    if (media) return media;
    var info = row.querySelector('.cart-item__product--info, .cart-item__details');
    if (!info || !info.parentNode) return null;
    var box = document.createElement('div');
    box.className = 'cart-item__media blocks-radius media-wrapper pallet-preview--standalone';
    info.parentNode.insertBefore(box, info);
    return box;
  }
  function scopeOf(row) { return row.closest('cart-drawer-items, cart-items, cart-drawer, form, table') || document; }

  /* ---------- painting ---------- */
  function paint(row) {
    var handle = handleOf(row);
    if (ONE_LINE_RE.test(handle)) return paintOneLine(row);
    if (handle !== HANDLE) return;
    var opts = optionsOf(row), line = opts[0] || '', qty = quantityOf(row), code = codeOf(line), spec;
    var id = configIdOf(row), markup, key;

    if (!id) { /* legacy line */
      if (code === '_ship') markup = deliverySvg(opts[1], 1);
      else if (code && (spec = specOf(code))) markup = stackSvg(spec, qty);
      else return;
      key = 'L|' + line + '|' + (opts[1] || '') + '|' + qty;
    } else {
      var siblings = Array.prototype.filter.call(scopeOf(row).querySelectorAll('.cart-item'), function (x) {
        return handleOf(x) === HANDLE && configIdOf(x) === id;
      });
      if (siblings[0] === row) {
        var cfg = CONFIGS[id];
        if (!cfg && !loadedOnce) loadConfigs();
        var fromRows = !cfg;
        if (fromRows) cfg = configFromRows(siblings);
        markup = configSvg(cfg, id);
        key = 'C|' + id + '|' + (fromRows ? 'rows:' + siblings.map(function (x) { return optionsOf(x)[0] + quantityOf(x); }).join(',') : 'json');
      } else if (code === '_ship') {
        markup = deliverySvg(opts[1], qty);
        key = 'S|' + id + '|' + (opts[1] || '') + '|' + qty;
      } else if (code && code.charAt(0) !== '_' && (spec = specOf(code))) {
        markup = boxSvg(spec, qty);
        key = 'B|' + id + '|' + code + '|' + qty;
      } else return; /* adjustment line keeps its image */
    }
    if (row.getAttribute('data-pallet-preview') === key) return;
    var slot = slotFor(row);
    if (!slot) return;
    slot.classList.add('pallet-preview');
    slot.innerHTML = markup;
    row.setAttribute('data-pallet-preview', key);
  }

  /* the one line from PetCan Ops: the whole configuration from _pb_json */
  function paintOneLine(row) {
    var id = configIdOf(row);
    if (!id) return;
    var cfg = CONFIGS[id];
    if (!cfg) { if (!loadedOnce) loadConfigs(); return; } /* keeps the product photo */
    var key = 'O|' + id;
    if (row.getAttribute('data-pallet-preview') === key) return;
    var slot = slotFor(row);
    if (!slot) return;
    slot.classList.add('pallet-preview');
    slot.innerHTML = configSvg(cfg, id);
    row.setAttribute('data-pallet-preview', key);
  }

  function paintAll() {
    var rows = document.querySelectorAll('.cart-item');
    for (var i = 0; i < rows.length; i++) paint(rows[i]);
  }
  function forceRepaint() {
    var rows = document.querySelectorAll('.cart-item[data-pallet-preview]');
    for (var i = 0; i < rows.length; i++) rows[i].removeAttribute('data-pallet-preview');
    schedule();
  }

  var pending = null;
  function schedule() {
    if (pending) return;
    pending = requestAnimationFrame(function () { pending = null; paintAll(); });
  }
  /* after a cart change the configurations may differ: read /cart.js again */
  function refresh() { loadedOnce = false; loadConfigs(); schedule(); }

  function start() {
    if (document.querySelector('.cart-item')) loadConfigs();
    paintAll();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (ev) { document.addEventListener(ev, refresh); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
